// Once https://github.com/github/primer/issues/1142 is fixed this can be removed
import {sx, themeGet} from '@primer/styled-react'
import type {SxProp} from '@primer/styled-react'
import React from 'react'
import styled from 'styled-components'

// TODO: remove invalid "extralarge" size name in next breaking change
/** @deprecated 'extralarge' to be removed to align with size naming ADR https://github.com/github/primer/blob/main/adrs/2022-02-09-size-naming-guidelines.md **/
type ExtraLarge = 'extralarge'
type TokenSizeKeys = 'small' | 'medium' | 'large' | 'xlarge' | ExtraLarge

const xlargeSize = '32px'

const tokenSizes: Record<TokenSizeKeys, string> = {
  small: '16px',
  medium: '20px',
  large: '24px',
  extralarge: xlargeSize,
  xlarge: xlargeSize,
}

export const defaultTokenSize: TokenSizeKeys = 'medium'

export interface TokenBaseProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement | HTMLButtonElement | HTMLAnchorElement>,
  'size' | 'id'
> {
  as?: 'button' | 'a' | 'span'
  ref?: React.Ref<TokenElements>
  /**
   * The function that gets called when a user clicks the remove button, or keys "Backspace" or "Delete" when focused on the token
   */
  onRemove?: () => void
  /**
   * Whether the remove button should be rendered in the token
   */
  hideRemoveButton?: boolean
  /**
   * Whether the token is selected
   */
  isSelected?: boolean
  /**
   * The text label inside the token
   */
  text: string | React.ReactElement
  /**
   * A unique identifier that can be associated with the token
   */
  id?: number | string
  /**
   * Which size the token will be rendered at
   */
  size?: TokenSizeKeys
  /**
   * Override the default token interaction styles
   */
  interactive?: boolean
}

type TokenElements = HTMLSpanElement | HTMLButtonElement | HTMLAnchorElement

export const isTokenInteractive = ({
  as = 'span',
  onClick,
  onFocus,
  tabIndex = -1,
  interactive,
}: Pick<TokenBaseProps, 'as' | 'onClick' | 'onFocus' | 'tabIndex' | 'interactive'>) =>
  interactive ?? Boolean(onFocus || onClick || tabIndex > -1 || ['a', 'button'].includes(as))

const xlargeVariantStyles = {
  fontSize: 1,
  height: tokenSizes.xlarge,
  lineHeight: tokenSizes.xlarge,
  px: 3,
  py: 0,
}

const variants: Record<
  TokenSizeKeys,
  {
    fontSize: number
    height: string
    // without setting lineHeight to match height, the "x" appears vertically mis-aligned
    lineHeight: string
    px: number
    // need to explicitly set padding top and bottom to "0" to override default `<button>` element styles
    // without setting these, the "x" appears vertically mis-aligned
    py: number
  }
> = {
  small: {
    fontSize: 0,
    height: tokenSizes.small,
    lineHeight: tokenSizes.small,
    px: 1,
    py: 0,
  },
  medium: {
    fontSize: 0,
    height: tokenSizes.medium,
    lineHeight: tokenSizes.medium,
    px: 2,
    py: 0,
  },
  large: {
    fontSize: 0,
    height: tokenSizes.large,
    lineHeight: tokenSizes.large,
    px: 2,
    py: 0,
  },
  extralarge: xlargeVariantStyles,
  xlarge: xlargeVariantStyles,
}

const StyledTokenBase = styled.span<SxProp>`
  align-items: center;
  /* stylelint-disable-next-line primer/borders */
  border-radius: 999px;
  cursor: ${props => (isTokenInteractive(props) ? 'pointer' : 'auto')};
  display: inline-flex;
  /* stylelint-disable-next-line primer/typography */
  font-weight: ${themeGet('fontWeights.bold')};
  font-family: inherit;
  text-decoration: none;
  max-width: 100%;
  position: relative;
  white-space: nowrap;
  ${sx}
`

function TokenBase({
  ref: forwardedRef,
  text,
  onRemove,
  id,
  as = 'span',
  sx: baseSx,
  size = defaultTokenSize,
  ...rest
}: TokenBaseProps & SxProp & {size?: TokenSizeKeys}) {
  return (
    <StyledTokenBase
      aria-label={onRemove ? `${text}, press backspace or delete to remove` : undefined}
      id={id?.toString()}
      as={as}
      sx={{
        ...variants[size],
        ...baseSx,
      }}
      {...rest}
      ref={forwardedRef}
    />
  )
}

export default TokenBase

try{ TokenBase.displayName ||= 'TokenBase' } catch {}